import type { ApiResponse, Pagination } from "@/types";

import { BaseApi } from "../base.api";

export type PostType = "NEWS" | "BLOG";
export type PostStatus = "DRAFT" | "PENDING_REVIEW" | "PUBLISHED" | "REJECTED";

export interface PostAuthor {
  id: number;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  role?: string;
}

export interface Post {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  coverImage: string | null;
  category: string | null;
  tags: string[] | null;
  type: PostType;
  status: PostStatus;
  viewCount: number;
  likeCount: number;
  commentCount: number;
  likedByMe?: boolean;
  authorId: number;
  author?: PostAuthor;
  publishedAt: string | null;
  /** Moderatsiyaga yuborilgan payt — navbat shu bo'yicha tartiblanadi. */
  submittedAt?: string | null;
  rejectionReason: string | null;
  reviewedAt: string | null;
  reviewedBy?: { id: number; firstName: string; lastName: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface PostComment {
  id: number;
  postId: number;
  userId: number;
  content: string;
  createdAt: string;
  user?: { id: number; firstName: string; lastName: string; avatarUrl: string | null };
}

export interface LikeState {
  liked: boolean;
  likeCount: number;
}

export interface CreatePostData {
  title: string;
  content: string;
  type: PostType;
  excerpt?: string;
  coverImage?: string | null;
  category?: string;
  tags?: string[];
  publish?: boolean;
}

export type UpdatePostData = Partial<Omit<CreatePostData, "type" | "publish">>;

export interface PostFilter {
  page?: number;
  limit?: number;
  type?: PostType;
  status?: PostStatus;
  category?: string;
  tag?: string;
  search?: string;
  sort?: "recent" | "popular";
}

export interface PostModerationStats {
  pending: number;
  published: number;
  rejected: number;
}

/** Yangiliklar va bloglar API — backend'dagi Posts kontrollerlarining ko'zgusi. */
export class PostsApi extends BaseApi {
  private readonly baseUrl: string = "/posts";

  // ─── Ommaviy ──────────────────────────────────────────────────────────────
  listNews(filter: PostFilter = {}) {
    return this.request.get<Post[], Pagination<Post>>("/news", { params: filter });
  }

  getNews(slug: string) {
    return this.request.get<Post, ApiResponse<Post>>(`/news/${slug}`);
  }

  newsCategories() {
    return this.request.get<string[], ApiResponse<string[]>>("/news/categories");
  }

  listBlogs(filter: PostFilter = {}) {
    return this.request.get<Post[], Pagination<Post>>("/blogs", { params: filter });
  }

  getBlog(slug: string) {
    return this.request.get<Post, ApiResponse<Post>>(`/blogs/${slug}`);
  }

  blogCategories() {
    return this.request.get<string[], ApiResponse<string[]>>("/blogs/categories");
  }

  // ─── Muallif ────────────────────────────────────────────────────────────────
  listMine(filter: PostFilter = {}) {
    return this.request.get<Post[], Pagination<Post>>(`${this.baseUrl}/mine`, { params: filter });
  }

  getOne(id: number) {
    return this.request.get<Post, ApiResponse<Post>>(`${this.baseUrl}/${id}`);
  }

  create(data: CreatePostData) {
    return this.request.post<Post, ApiResponse<Post>>(this.baseUrl, data);
  }

  update(id: number, data: UpdatePostData) {
    return this.request.patch<Post, ApiResponse<Post>>(`${this.baseUrl}/${id}`, data);
  }

  remove(id: number) {
    return this.request.delete<null, ApiResponse<null>>(`${this.baseUrl}/${id}`);
  }

  submit(id: number) {
    return this.request.post<Post, ApiResponse<Post>>(`${this.baseUrl}/${id}/submit`, {});
  }

  // ─── Xodimlar ─────────────────────────────────────────────────────────────
  publish(id: number) {
    return this.request.post<Post, ApiResponse<Post>>(`${this.baseUrl}/${id}/publish`, {});
  }

  unpublish(id: number) {
    return this.request.post<Post, ApiResponse<Post>>(`${this.baseUrl}/${id}/unpublish`, {});
  }

  approve(id: number) {
    return this.request.post<Post, ApiResponse<Post>>(`${this.baseUrl}/${id}/approve`, {});
  }

  reject(id: number, reason: string) {
    return this.request.post<Post, ApiResponse<Post>>(`${this.baseUrl}/${id}/reject`, { reason });
  }

  moderationStats() {
    return this.request.get<PostModerationStats, ApiResponse<PostModerationStats>>(
      `${this.baseUrl}/moderation/stats`,
    );
  }

  moderationList(filter: PostFilter = {}) {
    return this.request.get<Post[], Pagination<Post>>(`${this.baseUrl}/moderation/list`, {
      params: filter,
    });
  }

  // ─── Izohlar va layklar ─────────────────────────────────────────────────────
  listComments(postId: number, filter: { page?: number; limit?: number } = {}) {
    return this.request.get<PostComment[], Pagination<PostComment>>(
      `${this.baseUrl}/${postId}/comments`,
      { params: filter },
    );
  }

  addComment(postId: number, content: string) {
    return this.request.post<PostComment, ApiResponse<PostComment>>(
      `${this.baseUrl}/${postId}/comments`,
      { content },
    );
  }

  deleteComment(commentId: number) {
    return this.request.delete<null, ApiResponse<null>>(`${this.baseUrl}/comments/${commentId}`);
  }

  likeState(postId: number) {
    return this.request.get<LikeState, ApiResponse<LikeState>>(`${this.baseUrl}/${postId}/likes`);
  }

  toggleLike(postId: number) {
    return this.request.post<LikeState, ApiResponse<LikeState>>(
      `${this.baseUrl}/${postId}/like`,
      {},
    );
  }
}

export const postsApi = new PostsApi();
